import { useState, useRef, useEffect, useCallback } from 'react';

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

export function useFixedDropdown(optionsCount: number) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ 
    top: 0, 
    left: 0, 
    width: 0, 
    maxHeight: 300
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const gap = 4;
      
      // Always position below the trigger
      const top = rect.bottom + gap;
      
      // Calculate available space below
      const spaceBelow = viewportHeight - top - 20; // 20px margin from bottom
      
      // Set max height to available space, with a reasonable minimum
      const maxHeight = Math.max(150, Math.min(400, spaceBelow));
      
      setDropdownPosition({
        top,
        left: rect.left,
        width: rect.width,
        maxHeight
      });
    }
  }, [optionsCount]);

  const handleOpen = () => {
    calculatePosition();
    setIsOpen(true);
  };

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      calculatePosition();
      setIsOpen(true);
    }
  };

  // Handle scroll and resize events to reposition dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => calculatePosition();
    const handleResize = () => calculatePosition();

    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, calculatePosition]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current && 
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return {
    isOpen,
    setIsOpen,
    dropdownPosition,
    containerRef,
    triggerRef,
    dropdownRef,
    handleOpen,
    handleToggle,
    calculatePosition
  };
} 